import { Platform, View } from 'react-native';
import React, { useState } from 'react';

import { AcceptOrder } from '@components/AcceptOrder';
import { Button } from '@components/Button';
import { RejectOrder } from '@components/RejectOrder';
import { Typography } from '@components/Typography';
import { firebase } from '@lib/firebase';
import { logger } from '@lib/logger';
import { makeStyles } from '@providers/Theme';
import { trackEvent } from '@utils/analytics';
import { updateOrderStatus } from '@lib/api';
import { useToast } from '@providers/Toast';
import { useTranslation } from '@providers/I18n';
import { Order, OrderStateEnum } from '@lib/types';

const useStyles = makeStyles((theme) => ({
  actions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    paddingVertical: theme.spacing(1),
    backgroundColor: theme.palette.grey[200],
    boxShadow: 'rgb(0 0 0 / 57%) 0px 0px 10px -5px',
    elevation: 8,
    marginBottom: theme.spacing(Platform.OS === 'web' ? 0 : 3), // to fix issue in tablet & mobile
  },
}));

export const OrderActions: React.FC<{ order: Order | null }> = ({ order }) => {
  const toast = useToast();
  const styles = useStyles();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toastId = 'order-action-toast'; // Reusable toast ID

  if (!order) return null;

  const orderUUID = order?.orderMessage?.uuid;
  const orderState = order?.orderMessage?.data?.state;

  const handleUpdateStatus = async (
    requestType: 'SET_ORDER_AS_OUT_FOR_DELIVERY' | 'SET_ORDER_AS_FOOD_IS_READY',
    successTitle: string,
    successMessage: string,
    onSuccess?: () => void
  ) => {
    try {
      setIsSubmitting(true);
      toast.show(
        toastId,
        'loading',
        <Typography>{t('orderView.common.loading', 'Processing...')}</Typography>,
        <Typography>{t('orderView.common.updatingStatus', 'Updating order status...')}</Typography>
      );

      await updateOrderStatus({
        requestType,
        ordersUUIDs: [orderUUID],
        time: Date.now(),
      });

      onSuccess?.();

      toast.show(
        toastId,
        'success',
        <Typography>{successTitle}</Typography>,
        <Typography>{successMessage}</Typography>
      );
    } catch (err) {
      logger.error(`Error in ${requestType}:`, err);
      toast.show(
        toastId,
        'error',
        <Typography>{t('orderView.common.toast.error.title', 'Something went wrong!')}</Typography>,
        <Typography>{t('orderView.common.toast.error.message', 'Please try again later.')}</Typography>
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const setOrderAsOutForDelivery = () => {
    handleUpdateStatus(
      'SET_ORDER_AS_OUT_FOR_DELIVERY',
      t('orderView.outForDelivery.toast.success.title', 'Out for delivery'),
      t('orderView.outForDelivery.toast.success.message', 'Order status changed successfully')
    );
  };

  const setOrderAsFoodIsReady = () => {
    handleUpdateStatus(
      'SET_ORDER_AS_FOOD_IS_READY',
      t('orderView.foodIsReady.toast.success.title', 'Food is Ready'),
      t('orderView.foodIsReady.toast.success.message', 'Order status changed successfully'),
      () => {
        // Analytics with safety defaults
        trackEvent('clicked_food_is_ready', {
          order_short_code: order?.orderMessage?.data?.orderShortCode || 'N/A',
          order_status: order?.orderMessage?.data?.state || 'UNKNOWN',
          restaurant_id: order?.orderMessage?.ref?.restaurant?.restaurantId || 'N/A',
          branch_id: order?.orderMessage?.ref?.branch?.uuid || 'N/A',
        });
      }
    );
  };

  const hideFoodIsReady = firebase.getRemoteConfigBoolean('HIDE_FOOD_IS_READY_BUTTON');

  return (
    <View>
      {/* PENDING / ROUTED / SEEN */}
      {[OrderStateEnum.PENDING, OrderStateEnum.ROUTED, OrderStateEnum.SEEN].includes(orderState as any) && (
        <View style={styles.actions}>
          <RejectOrder order={order} />
          <AcceptOrder order={order} />
        </View>
      )}

      {/* ACCEPTED */}
      {orderState === OrderStateEnum.ACCEPTED && (
        <View style={styles.actions}>
          {!order?.orderMessage?.data?.fleet ? (
            <Button variant="primary" onPress={setOrderAsOutForDelivery} loading={isSubmitting}>
              {t('orderView.outForDeliveryButton', { defaultValue: 'Out for delivery' })}
            </Button>
          ) : (
            !hideFoodIsReady && (
              <Button variant="primary" onPress={setOrderAsFoodIsReady} loading={isSubmitting}>
                {t('orderView.foodIsReady', { defaultValue: 'Food is Ready' })}
              </Button>
            )
          )}
        </View>
      )}
    </View>
  );
};
